<template>
    <div class="card service--specific">
        <pilulka-tabs :active-tab-id="activeTab || 'tab-all'">
            <pilulka-tab
                tab-id="tab-all"
                url="#tab-all"
                :title="$t('productDetail.tabs.allTitle')"
                :default-active="activeTab === 'tab-all'"
            >
                <article>
                    <h2 v-if="productHasNewSeoBehavior">{{ $t('productDetail.reviewsForProduct') }} {{ productData.name }}</h2>
                    <RatingOverview
                        v-if="productData.productRating && productData.productRating?.totalReviews > 0"
                        :question-ratings="productData.comments.questionRatings"
                        :product-rating="productData.productRating"
                    />

                    <ReviewOverview v-if="reviewListData.length" :review-list-data="reviewListData" :max-count="2" />
                    <AllServiceDetail
                        class="mb-5 pt-3 pt-lg-4"
                        :other-manufacturer-services="otherManufacturerServices"
                        :description="description"
                        :comments-count="commentsCount"
                        :product-data="productData"
                    />
                    <AttributeWarningMessage :hygienic="productData.attributes.cannotBeReturnedForHygienicReasons" />
                    <AttributeWarningMessage :expiration="productData.hasExpiration" />
                    <AttributeWarningMessage :dietary-supplement="productData.attributes.isDietarySupplement" />
                    <AttributeWarningMessage :freely-available-medicine="productData.attributes.isFreelyAvailableMedicine" />
                    <AttributeWarningMessage :breast-feeding="productData.attributes.isBreastFeeding" />
                    <SafetyMessage :safety-message="productData.safetyMessage" />
                    <h2 v-if="productHasNewSeoBehavior">
                        {{
                            productData.content?.files[0]?.type === 1
                                ? $t('productDetail.additionalInfoAndLeafletFor')
                                : $t('productDetail.additionalInfoFor')
                        }}
                        {{ productData.name }}
                    </h2>
                    <Leaflet class="mb-2" :brand="productData.brand" :info="productData.additionalInformation" />
                </article>
            </pilulka-tab>

            <pilulka-tab
                v-if="productData.productRating && productData.productRating.totalReviews > 0"
                id="tab-reviews"
                :title="$t('productDetail.tabs.ratingTitle')"
                :url="!productHasNewSeoBehavior ? productTabLinks['tab-reviews'] : null"
                :default-active="activeTab === 'tab-reviews'"
                tab-id="tab-reviews"
            >
                <div class="card-text">
                    <RatingOverview
                        v-if="productData.productRating && productData.productRating.totalReviews > 0"
                        :question-ratings="productData.comments.questionRatings"
                        :product-rating="productData.productRating"
                    />

                    <WeTested :reviews="reviewListData" />

                    <RatingList :type="commentTypeProduct" :object-id="productId" :product-name="productData.name" />
                </div>
            </pilulka-tab>

            <pilulka-tab
                v-if="productData.content"
                id="tab-leaflet"
                :title="getLeafletTitle($t('productDetail.tabs.leafletTitle'), $t('productDetail.tabs.additionalInformation'))"
                :url="!productHasNewSeoBehavior && productData.showLeaflet ? productTabLinks['tab-leaflet'] : null"
                :default-active="activeTab === 'tab-leaflet'"
                tab-id="tab-leaflet"
            >
                <div class="card-text">
                    <div v-if="productData.content?.files[0]?.type === 1" class="text-center mb-5">
                        <a
                            v-if="productData.content.files[0].type === 1"
                            :href="productData.content.files[0].url"
                            :download="productData.content.files[0].name"
                            target="_blank"
                            class="btn btn-outline-primary button--download"
                        >
                            {{ $t('productDetail.tabs.leafletText') }}
                            <svg
                                class="ico--download"
                                width="16"
                                height="15"
                                viewBox="0 0 16 15"
                                fill="#E6007E"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M16 12.6667V10C16 9.82319 15.9298 9.65362 15.8047 9.52859C15.6797 9.40357 15.5101 9.33333 15.3333 9.33333C15.1565 9.33333 14.987 9.40357 14.8619 9.52859C14.7369 9.65362 14.6667 9.82319 14.6667 10V12.6667C14.6667 12.8435 14.5964 13.013 14.4714 13.1381C14.3464 13.2631 14.1768 13.3333 14 13.3333H2C1.82319 13.3333 1.65362 13.2631 1.5286 13.1381C1.40357 13.013 1.33333 12.8435 1.33333 12.6667V10C1.33333 9.82319 1.2631 9.65362 1.13807 9.52859C1.01305 9.40357 0.843478 9.33333 0.666667 9.33333C0.489856 9.33333 0.320287 9.40357 0.195262 9.52859C0.070238 9.65362 0 9.82319 0 10V12.6667C0 13.1971 0.210714 13.7058 0.585787 14.0809C0.960859 14.456 1.46957 14.6667 2 14.6667H14C14.5304 14.6667 15.0391 14.456 15.4142 14.0809C15.7893 13.7058 16 13.1971 16 12.6667ZM11.7467 9.18667L8.41333 11.8533C8.29564 11.9463 8.15001 11.9969 8 11.9969C7.84999 11.9969 7.70436 11.9463 7.58667 11.8533L4.25333 9.18667C4.13188 9.07202 4.05788 8.91596 4.04598 8.74937C4.03408 8.58278 4.08514 8.41778 4.18907 8.28704C4.29299 8.15629 4.44221 8.06932 4.60719 8.04332C4.77218 8.01733 4.94091 8.05421 5.08 8.14667L7.33333 9.94667V0.666667C7.33333 0.489856 7.40357 0.320287 7.5286 0.195262C7.65362 0.070238 7.82319 0 8 0C8.17681 0 8.34638 0.070238 8.47141 0.195262C8.59643 0.320287 8.66667 0.489856 8.66667 0.666667V9.94667L10.92 8.14667C10.9871 8.0833 11.0667 8.03456 11.1536 8.00355C11.2406 7.97253 11.333 7.95992 11.4251 7.9665C11.5172 7.97307 11.6069 7.9987 11.6886 8.04176C11.7702 8.08482 11.8421 8.14437 11.8995 8.21664C11.9569 8.28891 11.9987 8.37232 12.0223 8.46158C12.0458 8.55085 12.0505 8.64403 12.0362 8.73522C12.0218 8.82641 11.9886 8.91363 11.9388 8.99134C11.889 9.06905 11.8235 9.13556 11.7467 9.18667Z"
                                />
                            </svg>
                        </a>
                    </div>

                    <div v-if="productData.content.spc" class="mb-4" v-html="productData.content.spc"></div>

                    <Leaflet :brand="productData.brand" :info="productData.additionalInformation" />
                </div>
            </pilulka-tab>

            <pilulka-tab
                v-if="isDiscussionsEnabled"
                id="tab-discussion"
                :title="$t('productDetail.tabs.discussionTitle')"
                :url="!productHasNewSeoBehavior && discussionsCount > 0 ? productTabLinks['tab-discussion'] : null"
                :default-active="activeTab === 'tab-discussion'"
                tab-id="tab-discussion"
            >
                <div class="card-text">
                    <h3 class="my-4 text-center">{{ $t('productDetail.tabs.discussionText') }} {{ productData.name }}</h3>
                    <ObjectsDiscussionFeed
                        :product-id="productId"
                        :category-id="null"
                        :show-add-question="true"
                        :load-immediately="activeTab === 'tab-discussion'"
                    />
                </div>
            </pilulka-tab>
        </pilulka-tabs>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import RatingOverview from '~/components/elements/RatingOverview.vue'
import ReviewOverview from '~/components/elements/ReviewOverview.vue'
import AllServiceDetail from '~/components/layout/productDetail/AllServiceDetail.vue'
import RatingList from '~/components/layout/productDetail/RatingList.vue'
import CommentType from '~/value/CommentType'
import WeTested from '~/components/layout/productDetail/WeTested.vue'
import Leaflet from '~/components/layout/productDetail/Leaflet.vue'
import PilulkaTabs from '~/components/elements/PilulkaTabs.vue'
import PilulkaTab from '~/components/elements/PilulkaTab.vue'
import FullProduct from '~/models/Product/FullProduct'
import AttributeWarningMessage from '~/components/layout/productDetail/AttributeWarningMessage.vue'
import Review from '~/models/ReviewClub/Review'
import SafetyMessage from '~/components/layout/productDetail/SafetyMessage.vue'
export default {
    components: {
        SafetyMessage,
        AttributeWarningMessage,
        PilulkaTab,
        PilulkaTabs,
        AllServiceDetail,
        RatingList,
        WeTested,
        Leaflet,
        RatingOverview,
        ReviewOverview,
    },

    props: {
        activeTab: {
            type: String,
            default() {
                return ''
            },
        },

        productTabLinks: {
            type: Object,
            required: true,
        },

        productData: {
            type: Object as PropType<FullProduct>,
            required: true,
        },

        productId: {
            type: Number,
            default: null,
        },

        otherManufacturerServices: {
            type: Array,
            default() {
                return []
            },
        },

        description: {
            type: String,
            default() {
                return ''
            },
        },

        commentsCount: {
            type: Number,
            default: 0,
        },

        closestOffices: {
            type: Array,
            default() {
                return []
            },
        },

        reviewListData: {
            type: Array as PropType<Review[]>,
            required: true,
        },

        discussionsCount: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            showRatingWasAddedFlashMessage: false,
            visible: false,
            cssClass: 'variant-dropdown__container--shown',
            productHasNewSeoBehavior: hasProductNewSeoBehavior(this.productData.url),
        }
    },

    computed: {
        commentTypeProduct() {
            return CommentType.PRODUCT
        },

        isDiscussionsEnabled() {
            return this.$config.public.discussionsEnabled
        },
    },

    methods: {
        getLeafletTitle(leafletTitle: string, additionalInformation: string): string {
            if (this.productData.content.files.length) {
                return this.productData.content.files[0].type === 1 ? leafletTitle : additionalInformation
            }

            return additionalInformation
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../node_modules/bootstrap/scss/functions';
@import '../../../node_modules/bootstrap/scss/variables';
@import '../../../node_modules/bootstrap/scss/mixins';

.service--specific:deep {
    background: transparent;
    border: 0;
    padding: 0;

    .textContent > p {
        font-size: 16px;
        line-height: 24px;
        margin: 10px 0;
    }

    .textContent iframe {
        max-width: 100%;
        width: 100%;
        height: auto;
        aspect-ratio: 16 / 9;
    }

    .tab-content > .tab-pane {
        padding: 0;
    }

    .card-header {
        overflow-x: auto;
        background: transparent;
        padding: 0;
        border: 0;
    }

    .card-header-tabs {
        display: inline-flex;
        flex-wrap: nowrap;
        margin-bottom: 1em;
        margin-left: 0;
    }

    .tab-content .card-text {
        margin: 0;
        padding: 0;
    }

    @include media-breakpoint-down(sm) {
        padding: 0;

        .tab-content .card-body {
            padding-left: 0;
            padding-right: 0;
        }

        .lists .list.list--3col .list__item__title-link {
            padding-bottom: 0;
        }
    }

    .nav-item {
        margin-right: 0.5em;
        cursor: pointer;

        a {
            font-size: 14px;
            font-weight: 500;
        }
    }

    .nav-tabs .nav-link.active,
    .nav-tabs .nav-link,
    .nav-tabs .nav-item.show .nav-link {
        color: black;
        background: transparent;
        text-align: center;
        border: none;
        border-radius: 10px;
        white-space: nowrap;

        &:focus {
            outline: 0;
        }
    }

    .nav-tabs .nav-link {
        border: 1px solid transparent;
        color: $color-dark;
        padding: 0.5rem 1rem;

        &:hover {
            @media (min-width: 1024px) {
                background-color: $color-grey;
            }
        }

        &.active {
            color: $color-dark;
            background-color: $color-grey;
            border: 1px solid $color-grey-extra-dark;
        }
    }
}

.services-list__body {
    padding: 0;
}

.btn-lg {
    padding: 0 4rem;
    height: 50px;
    font-size: 13px;
    font-weight: 500;
}

.button--download {
    margin-top: 30px;
    padding: 6px 20px;
    font-size: 14px;
    font-weight: 600;

    &:hover:deep(.ico--download) {
        fill: #ffffff;
        transition: all 300ms;
    }
}

.ico--download {
    position: relative;
    top: -2px;
    margin-left: 8px;
}
</style>
